import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../context/auth-context";
import { useHttpClient } from "../../hooks/http-hook";
import { useWebSocketSubscription } from "../../context/websocket-context";
import { useLoading } from "../../context/LoadingContext";
import { QRCodeDisplay } from "./QRCodeDisplay";
import { WhatsAppStatus } from "./WhatsAppStatus";
import { ClinicaContext } from "../../context/clinica-context";

export const WhatsAppManager = () => {
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const { sendRequest } = useHttpClient();
  const { startLoading, stopLoading } = useLoading();

  const [statusData, setStatusData] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [error, setError] = useState(null);
  const [testPhone, setTestPhone] = useState("");

  // 🔌 WebSocket - Escutar eventos WhatsApp em tempo real
  useWebSocketSubscription(
    (parsedSocketData) => {
      // Filtrar apenas eventos WhatsApp da clínica atual
      if (
        parsedSocketData.tipo === "whatsapp_qrcode" &&
        parsedSocketData.clinicaId === clinica.clinica._id
      ) {
        console.log("📲 QR Code recebido via WebSocket");
        setQrCode(parsedSocketData.qrCode);
        setConnectionStatus("awaiting_scan");
      }

      if (
        parsedSocketData.tipo === "whatsapp_connected" &&
        parsedSocketData.clinicaId === clinica.clinica._id
      ) {
        console.log("✅ WhatsApp conectado via WebSocket");
        setIsConnected(true);
        setConnectionStatus("connected");
        setQrCode(null);
        loadStatus(); // Recarregar status
      }

      if (
        parsedSocketData.tipo === "whatsapp_disconnected" &&
        parsedSocketData.clinicaId === clinica.clinica._id
      ) {
        console.log("❌ WhatsApp desconectado via WebSocket");
        setIsConnected(false);
        setConnectionStatus("disconnected");
        setQrCode(null);
        loadStatus(); // Recarregar status
      }
    },
    [clinica.clinica._id],
    "WhatsAppManager"
  );

  // 📊 Carregar status inicial
  const loadStatus = useCallback(async () => {
    try {
      const data = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/whatsapp/${clinica.clinica._id}/status`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      setStatusData(data);
      setIsConnected(data.sessionStatus?.connected || false);
      setConnectionStatus(
        data.sessionStatus?.connected ? "connected" : "disconnected"
      );
    } catch (err) {
      console.error("Erro ao carregar status:", err);
      setError("Erro ao carregar status do WhatsApp");
    }
  }, [clinica.clinica._id, auth.token, sendRequest]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // 🔗 Conectar WhatsApp
  const handleConnect = async () => {
    try {
      startLoading();
      setError(null);

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/whatsapp/${clinica.clinica._id}/connect`,
        "POST",
        null,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      setConnectionStatus("connecting");

      // Aguardar um pouco e tentar obter QR Code
      setTimeout(async () => {
        try {
          const qrData = await sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/whatsapp/${clinica.clinica._id}/qrcode`,
            "GET",
            null,
            {
              Authorization: "Bearer " + auth.token,
            }
          );

          if (qrData.qrCode) {
            setQrCode(qrData.qrCode);
            setConnectionStatus("awaiting_scan");
          }
        } catch (err) {
          console.error("Erro ao obter QR Code:", err);
        }
      }, 2000);
    } catch (err) {
      console.error("Erro ao conectar:", err);
      setError("Erro ao iniciar conexão. Tente novamente.");
    } finally {
      stopLoading();
    }
  };

  // 🔌 Desconectar WhatsApp
  const handleDisconnect = async () => {
    try {
      startLoading();
      setError(null);

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/whatsapp/${clinica.clinica._id}/disconnect`,
        "POST",
        null,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      setIsConnected(false);
      setConnectionStatus("disconnected");
      setQrCode(null);
      await loadStatus();
    } catch (err) {
      console.error("Erro ao desconectar:", err);
      setError("Erro ao desconectar. Tente novamente.");
    } finally {
      stopLoading();
    }
  };

  // 🔄 Reconectar WhatsApp
  const handleReconnect = async () => {
    try {
      startLoading();
      setError(null);

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/whatsapp/${clinica.clinica._id}/reconnect`,
        "POST",
        null,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      setConnectionStatus("connecting");

      setTimeout(async () => {
        try {
          const qrData = await sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/whatsapp/${clinica.clinica._id}/qrcode`,
            "GET",
            null,
            {
              Authorization: "Bearer " + auth.token,
            }
          );

          if (qrData.qrCode) {
            setQrCode(qrData.qrCode);
            setConnectionStatus("awaiting_scan");
          }
        } catch (err) {
          console.error("Erro ao obter QR Code:", err);
        }
      }, 2000);
    } catch (err) {
      console.error("Erro ao reconectar:", err);
      setError("Erro ao reconectar. Tente novamente.");
    } finally {
      stopLoading();
    }
  };

  // 📤 Enviar mensagem de teste
  const handleSendTest = async () => {
    if (!testPhone) {
      setError("Digite um número de telefone");
      return;
    }

    try {
      startLoading();
      setError(null);

      const body = JSON.stringify({ phoneNumber: testPhone });

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/whatsapp/${clinica.clinica._id}/test`,
        "POST",
        body,
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        }
      );

      alert("✅ Mensagem de teste enviada com sucesso!");
      setTestPhone("");
      await loadStatus(); // Atualizar estatísticas
    } catch (err) {
      console.error("Erro ao enviar teste:", err);
      setError("Erro ao enviar mensagem de teste. Verifique o número.");
    } finally {
      stopLoading();
    }
  };

  if (!statusData) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-800">
            Gerenciamento WhatsApp
          </h2>
          <WhatsAppStatus
            status={isConnected ? "connected" : connectionStatus}
          />
        </div>

        {statusData.clinicaNome && (
          <p className="text-gray-600">
            Clínica: <strong>{statusData.clinicaNome}</strong>
          </p>
        )}

        {statusData.dbStatus?.numeroConectado && (
          <p className="text-gray-600">
            Número conectado:{" "}
            <strong>+{statusData.dbStatus.numeroConectado}</strong>
          </p>
        )}

        {statusData.nrWhatsapp && (
          <p className="text-gray-600">
            Número configurado: <strong>+238{statusData.nrWhatsapp}</strong>
          </p>
        )}
      </div>

      {/* Mensagens de erro */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">❌ {error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Fechar
          </button>
        </div>
      )}

      {/* QR Code ou Controles */}
      {qrCode && !isConnected ? (
        <QRCodeDisplay qrCode={qrCode} onRefresh={handleConnect} />
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Controles</h3>

          <div className="flex gap-3 mb-6">
            {!isConnected ? (
              <button
                onClick={handleConnect}
                className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                🔗 Conectar WhatsApp
              </button>
            ) : (
              <>
                <button
                  onClick={handleDisconnect}
                  className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  🔌 Desconectar
                </button>
                <button
                  onClick={handleReconnect}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  🔄 Reconectar
                </button>
              </>
            )}
          </div>

          {/* Teste de Envio */}
          {isConnected && (
            <div className="border-t pt-6">
              <h4 className="font-semibold mb-3">Enviar Mensagem de Teste</h4>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="Ex: 9999999"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleSendTest}
                  disabled={!testPhone}
                  className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  📤 Enviar Teste
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Digite apenas os dígitos (sem prefixo +238)
              </p>
            </div>
          )}
        </div>
      )}

      {/* Estatísticas */}
      {statusData.stats && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Estatísticas de Hoje</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-gray-600 mb-1">Enviadas (Total)</p>
              <p className="text-2xl font-bold text-green-600">
                {statusData.stats.mensagensEnviadas?.total || 0}
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-600 mb-1">Enviadas Hoje</p>
              <p className="text-2xl font-bold text-blue-600">
                {statusData.stats.mensagensEnviadas?.hoje || 0}
              </p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-gray-600 mb-1">Falhas (Total)</p>
              <p className="text-2xl font-bold text-red-600">
                {statusData.stats.mensagensFalhadas?.total || 0}
              </p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-gray-600 mb-1">Falhas Hoje</p>
              <p className="text-2xl font-bold text-orange-600">
                {statusData.stats.mensagensFalhadas?.hoje || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Configurações */}
      {statusData.whatsappConfig && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Configurações</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Enviar Confirmação</span>
              <span
                className={`px-3 py-1 rounded-full text-sm ${
                  statusData.whatsappConfig.enviarConfirmacao
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {statusData.whatsappConfig.enviarConfirmacao
                  ? "✅ Ativo"
                  : "❌ Inativo"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Enviar Lembretes</span>
              <span
                className={`px-3 py-1 rounded-full text-sm ${
                  statusData.whatsappConfig.enviarLembretes
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {statusData.whatsappConfig.enviarLembretes
                  ? "✅ Ativo"
                  : "❌ Inativo"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Horas Antes do Lembrete</span>
              <span className="font-semibold">
                {statusData.whatsappConfig.horasAntesLembrete}h
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
